import { useMemo, useState } from 'react';

import { ActionTabItem, ActionTabs, NUGGET_VARIANTS, toolTransform } from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { getShareUrl } from '@backrs_ui/grow-ui/src/components/Nuggets/nuggetLinks';
import { getBookmarkIdForNugget } from '@backrs_ui/grow-ui/src/pages/Bookmarks/pages/bookmarks.util';
import {
  useDeleteBookmarkMutation,
  useGetBookmarksQuery,
  usePostBookmarkMutation,
} from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';
import { useGetToolsQuery } from '@backrs_ui/grow-ui/src/redux/tools/tools.api';

import { Snackbar, useMediaQuery } from '@mui/material';

const STRINGS = appStrings.modules.satActBooster.results.actions;
const NUGGET_STRINGS = commonAppStrings.modules.nugget.dialogNuggetActions;
const ACTION_STRINGS = commonAppStrings.modules.actionTabs;

const TOOL_ID = 'sat_act_booster';

export const BoosterResultActions = () => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery('(max-width:600px)');

  const { data: tools } = useGetToolsQuery(undefined);
  const { data: bookmarks, isLoading: bookmarksLoading } = useGetBookmarksQuery(undefined);
  const [postBookmark] = usePostBookmarkMutation();
  const [deleteBookmark] = useDeleteBookmarkMutation();
  const [bookmarkError, setBookmarkError] = useState(false);
  const [showCopySuccess, setShowCopySuccess] = useState(false);

  const tool = tools?.find((candidate) => candidate.tool_id === TOOL_ID);
  const nugget = useMemo(() => (tool ? toolTransform(tool) : undefined), [tool]);

  const bookmarkId = getBookmarkIdForNugget(bookmarks, tool?.id, NUGGET_VARIANTS.TOOL);

  // The catalog row is the thing being bookmarked, so there is nothing to act on without it.
  if (!nugget) {
    return null;
  }

  const handleBookmark = async () => {
    try {
      if (bookmarkId) {
        await deleteBookmark(bookmarkId).unwrap();
      } else {
        await postBookmark({ type: 'tool', itemId: nugget.id }).unwrap();
      }
    } catch (error) {
      console.error('Bookmark toggle failed:', error);
      setBookmarkError(true);
    }
  };

  const handleShare = () => navigator.clipboard.writeText(getShareUrl({ contentNugget: nugget }));

  const handleAddTask = () => {
    dispatch(openPlaylistListStep());
    dispatch(setNuggetToAdd(nugget));
  };

  const bookmarkAction: ActionTabItem = {
    id: 'booster-result-bookmark-button',
    icon: bookmarkId ? 'BookmarkCheck' : 'Bookmark',
    onClick: handleBookmark,
    disabled: bookmarksLoading,
    tooltipHoverText: ACTION_STRINGS.bookmark,
  };

  const shareAction: ActionTabItem = {
    id: 'booster-result-share-button',
    icon: 'Share06',
    onClick: handleShare,
    tooltipOnClickText: NUGGET_STRINGS.linkCopied,
    tooltipHoverText: ACTION_STRINGS.share,
  };

  const addTaskAction: ActionTabItem = {
    id: 'booster-result-add-task-button',
    icon: 'BookmarkAdd',
    onClick: handleAddTask,
    tooltipHoverText: ACTION_STRINGS.task,
  };

  // The task dialog has no mobile layout, so the entry point is desktop-only.
  const actions: ActionTabItem[] = [
    bookmarkAction,
    shareAction,
    ...(isMobile ? [] : [addTaskAction]),
  ];

  return (
    <>
      <ActionTabs
        actions={actions}
        borderColor="Colors/Border/border-primary"
        showCopySuccess={showCopySuccess}
        onCopySuccessChange={setShowCopySuccess}
      />
      <Snackbar
        open={bookmarkError}
        onClose={() => setBookmarkError(false)}
        autoHideDuration={3000}
        message={STRINGS.bookmarkError}
      />
    </>
  );
};
